"use client";

import { useState } from "react";
import { Outlet, useLocation, useNavigate, NavLink, Link } from "react-router";
import { toast } from "react-toastify";
import FloatingAppointmentBtn from "../../components/FloatingAppointmentBtn";

import { Dialog, DialogPanel } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { logout } from "../../features/auth/authSlice";
import { services } from "../../data/services";

const navigation = [
  { name: "Home", href: "/" },
  { name: "AboutUs", href: "/about" },
  { name: "Services", href: "/services" },
  { name: "Nutrition & Dietetics", href: "/nutrition-and-dietetics" },
  { name: "ContactUs", href: "/contact" },
];

export default function Layout(): JSX.Element {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { user, defaultRole } = useAppSelector((state) => state.auth);

  const handleLogout = () => {
    dispatch(logout());
    toast.info("👋 Successfully logged out!");
    navigate("/");
  };

  return (
    <div className="bg-white relative">
      <FloatingAppointmentBtn />
      {/* Header */}
      <header className="bg-indigo-600 text-white sticky top-0 z-50">
        <nav
          aria-label="Global"
          className="flex items-center justify-between"
        >
          <div className="flex lg:flex-1">
            <Link to="/" className="p-1.5">
              <span className="sr-only">Your Company</span>
              <img
                alt=""
                src="/logo.png"
                className="h-16 w-auto border-b-2 border-indigo-600"
              />
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="flex lg:hidden">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="inline-flex items-center justify-center rounded-md p-2.5 text-gray-200 hover:bg-indigo-700"
            >
              <span className="sr-only">Open main menu</span>
              <Bars3Icon aria-hidden="true" className="size-6" />
            </button>
          </div>

          {/* Desktop menu */}
          <div className="hidden lg:flex lg:gap-x-12">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className="text-sm font-semibold text-white hover:text-gray-200"
              >
                {item.name}
              </NavLink>
            ))}
          </div>

          {/* Desktop login */}
          <div className="hidden lg:flex lg:flex-1 lg:justify-end lg:mr-12">
            {user ? (
              <button
                onClick={() => navigate("/dashboard")}
                className="p-1 px-3 text-white bg-green-600 rounded-lg hover:bg-green-700 transition-all duration-300 animate-gentle-pulse"
              >
                {defaultRole === "patient" ? "Book Appointment" : "Dashboard"}
              </button>
            ) : (
              location.pathname !== "/appointment" && (
                <NavLink
                  to="/appointment"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-semibold text-white hover:text-gray-200"
                >
                  Book Appointment <span aria-hidden="true">→</span>
                </NavLink>
              )
            )}
          </div>
        </nav>

        {/* Mobile menu drawer */}
        <Dialog
          open={mobileMenuOpen}
          onClose={setMobileMenuOpen}
          className="lg:hidden"
        >
          <div className="fixed inset-0 z-50 bg-black/50" />
          <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full max-w-sm overflow-y-auto bg-white p-6 shadow-lg sm:ring-1 sm:ring-gray-900/10">
            <div className="flex items-center justify-between">
              <a href="#" className="-m-1.5 p-1.5">
                <span className="sr-only">Your Company</span>
                <img
                  alt=""
                  src="/logo.png"
                  className="h-8 w-auto"
                />
              </a>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="rounded-md p-2.5 text-gray-700 hover:bg-gray-100"
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>

            <div className="mt-6 flow-root">
              <div className="-my-6 divide-y divide-gray-500/10">
                <div className="space-y-2 py-6">
                  {navigation.map((item) => (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50"
                    >
                      {item.name}
                    </NavLink>
                  ))}
                </div>
                <div className="py-6">
                  {user ? (
                    <button
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                      className="-mx-3 block w-full text-left rounded-lg px-3 py-2.5 text-base font-semibold text-gray-900 hover:bg-gray-50"
                    >
                      Logout
                    </button>
                  ) : (
                    location.pathname !== "/appointment" && (
                      <NavLink
                        to="/appointment"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setMobileMenuOpen(false)}
                        className="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold text-gray-900 hover:bg-gray-50"
                      >
                        Book Appointment
                      </NavLink>
                    )
                  )}
                </div>
              </div>
            </div>
          </DialogPanel>
        </Dialog>
      </header>

      <Outlet />

      <footer className="bg-indigo-600 p-5 text-white flex flex-col gap-y-8">
        <div className="flex flex-col gap-y-5 my-3 items-center justify-between md:flex-row">
          <img
            alt="logo"
            loading="lazy"
            width="50"
            height="50"
            decoding="async"
            src="/logo.png"
          />

          <div className="flex gap-x-5">
            <a href="" className="text-white hover:text-gray-300">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 16 16">
                <path d="M5.026 15c6.038..." />
              </svg>
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <h3 className="font-semibold text-lg mb-5 lg:mb-8 underline underline-offset-8 decoration-primary decoration-4">
              About Hospital
            </h3>
            <p className="text-sm">
              We are passionate about helping athletes and active individuals
              recover from injuries, enhance performance, and achieve their
              goals. Our team of experienced doctors, therapists, and
              specialists combines advanced medical techniques with personalized
              care.
            </p>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-5 lg:mb-8 underline underline-offset-8 decoration-primary decoration-4">
              Our Services
            </h3>
            <ul className="flex flex-col gap-y-3 text-sm">
              {services.map((service) => (
                <NavLink
                  key={service.slug}
                  to={`/service-details/${service.slug}`}
                  className="hover:text-white"
                >
                  {service.title}
                </NavLink>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-lg mb-5 lg:mb-8 underline underline-offset-8 decoration-primary decoration-4">
              Opening Hours
            </h3>
            <ul className="flex flex-col text-sm divide-y divide-primary">
              <li className="flex justify-between p-2">
                <span>Monday</span>
                <span>5PM-9PM</span>
              </li>
              <li className="flex justify-between p-2">
                <span>Tuesday</span>
                <span>5PM-9PM</span>
              </li>
              <li className="flex justify-between p-2">
                <span>Wednesday</span>
                <span>5PM-9PM</span>
              </li>
              <li className="flex justify-between p-2">
                <span>Thursday</span>
                <span>5PM-9PM</span>
              </li>
              <li className="flex justify-between p-2">
                <span>Friday</span>
                <span>5PM-9PM</span>
              </li>
              <li className="flex justify-between p-2">
                <span>Saturday</span>
                <span>Closed</span>
              </li>
              <li className="flex justify-between p-2">
                <span>Sunday</span>
                <span>Closed</span>
              </li>
            </ul>
          </div>

          <div className="flex flex-col gap-y-4 text-sm">
            <h3 className="font-semibold text-lg mb-5 lg:mb-8 underline underline-offset-8 decoration-primary decoration-4">
              Contact Us
            </h3>
            <p>
              <b>Support : </b>
              <a className="hover:underline" href="tel:9159038012">
                +91 9159038012
              </a>
            </p>
            <p>
              <b>Email : </b>
              <a
                className="hover:underline"
                href="mailto:harish91093@gmail.com"
              >
                harish91093@gmail.com
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
